export type StepUpProof={
  ceremony_id:string;
  credential:Record<string,unknown>;
  prf_result:string;
};

export type LoginProof={
  ceremony_id:string;
  credential:Record<string,unknown>;
};

function b64urlEncode(buffer:ArrayBuffer|ArrayBufferView):string{
  const bytes=buffer instanceof ArrayBuffer?new Uint8Array(buffer):new Uint8Array(buffer.buffer,buffer.byteOffset,buffer.byteLength);
  let binary="";
  for(const byte of bytes)binary+=String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g,"-").replace(/\//g,"_").replace(/=+$/,"");
}

function b64urlDecode(value:string):Uint8Array{
  const normalized=value.replace(/-/g,"+").replace(/_/g,"/");
  const padded=normalized+"=".repeat((4-normalized.length%4)%4);
  const binary=atob(padded);
  return Uint8Array.from(binary,c=>c.charCodeAt(0));
}

export function prepareRequestOptions(raw:unknown):PublicKeyCredentialRequestOptions{
  const options=structuredClone(raw) as Record<string,any>;
  if(typeof options.challenge!=="string")throw new Error("Passkey challenge is missing.");
  options.challenge=b64urlDecode(options.challenge);

  if(Array.isArray(options.allowCredentials)){
    options.allowCredentials=options.allowCredentials.map((item:Record<string,any>)=>({
      ...item,
      id:typeof item.id==="string"?b64urlDecode(item.id):item.id,
    }));
  }

  const first=options.extensions?.prf?.eval?.first;
  if(typeof first==="string")options.extensions.prf.eval.first=b64urlDecode(first);
  return options as PublicKeyCredentialRequestOptions;
}


export function prepareCreationOptions(raw:unknown):PublicKeyCredentialCreationOptions{
  const options=structuredClone(raw) as Record<string,any>;

  if(typeof options.challenge!=="string")throw new Error("Passkey challenge is missing.");
  options.challenge=b64urlDecode(options.challenge);

  if(!options.user||typeof options.user.id!=="string")throw new Error("Passkey user id is missing.");
  options.user.id=b64urlDecode(options.user.id);

  if(Array.isArray(options.excludeCredentials)){
    options.excludeCredentials=options.excludeCredentials.map((item:Record<string,any>)=>({
      ...item,
      id:typeof item.id==="string"?b64urlDecode(item.id):item.id,
    }));
  }

  const first=options.extensions?.prf?.eval?.first;
  if(typeof first==="string")options.extensions.prf.eval.first=b64urlDecode(first);

  return options as PublicKeyCredentialCreationOptions;
}


export function serializeRegistrationCredential(
  credential:PublicKeyCredential
):Record<string,unknown>{
  const response=credential.response;

  if(!(response instanceof AuthenticatorAttestationResponse)){
    throw new Error("Unexpected passkey registration response.");
  }

  return{
    id:credential.id,
    rawId:b64urlEncode(credential.rawId),
    type:credential.type,
    authenticatorAttachment:(credential as any).authenticatorAttachment??null,
    clientExtensionResults:credential.getClientExtensionResults(),
    response:{
      clientDataJSON:b64urlEncode(response.clientDataJSON),
      attestationObject:b64urlEncode(response.attestationObject),
      transports:typeof response.getTransports==="function"
        ?response.getTransports()
        :[],
    },
  };
}


export function serializeCredential(credential:PublicKeyCredential):Record<string,unknown>{
  const response=credential.response;
  if(!(response instanceof AuthenticatorAssertionResponse)){
    throw new Error("Unexpected passkey response.");
  }
  return{
    id:credential.id,
    rawId:b64urlEncode(credential.rawId),
    type:credential.type,
    authenticatorAttachment:(credential as any).authenticatorAttachment??null,
    clientExtensionResults:credential.getClientExtensionResults(),
    response:{
      clientDataJSON:b64urlEncode(response.clientDataJSON),
      authenticatorData:b64urlEncode(response.authenticatorData),
      signature:b64urlEncode(response.signature),
      userHandle:response.userHandle?b64urlEncode(response.userHandle):null,
    },
  };
}

function prfResult(credential:PublicKeyCredential):string|null{
  const extensions=credential.getClientExtensionResults() as any;
  const first=extensions?.prf?.results?.first;
  if(first instanceof ArrayBuffer||ArrayBuffer.isView(first))return b64urlEncode(first);
  return null;
}

export async function runPrfCeremony(ceremonyId:string,rawPublicKey:unknown):Promise<StepUpProof>{
  if(!window.PublicKeyCredential||!navigator.credentials){
    throw new Error("Passkeys are not supported by this browser.");
  }
  const credential=await navigator.credentials.get({
    publicKey:prepareRequestOptions(rawPublicKey),
  });
  if(!(credential instanceof PublicKeyCredential)){
    throw new Error("Passkey approval was cancelled.");
  }
  const prf=prfResult(credential);
  if(!prf){
    throw new Error("This passkey can sign in but cannot unlock the encrypted vault.");
  }
  return{
    ceremony_id:ceremonyId,
    credential:serializeCredential(credential),
    prf_result:prf,
  };
}

export async function runLoginCeremony(options:{ceremony_id:string;publicKey:unknown}):Promise<LoginProof>{
  if(!window.PublicKeyCredential||!navigator.credentials){
    throw new Error("Passkeys are not supported by this browser.");
  }
  const credential=await navigator.credentials.get({
    publicKey:prepareRequestOptions(options.publicKey),
  });
  if(!(credential instanceof PublicKeyCredential)){
    throw new Error("Passkey approval was cancelled.");
  }
  return{
    ceremony_id:options.ceremony_id,
    credential:serializeCredential(credential),
  };
}


export async function runRegistrationCeremony(
  options:{ceremony_id:string;publicKey:unknown}
):Promise<{
  ceremony_id:string;
  new_credential:Record<string,unknown>;
  new_prf_result:string;
}>{
  if(!window.PublicKeyCredential||!navigator.credentials){
    throw new Error("Passkeys are not supported by this browser.");
  }

  const credential=await navigator.credentials.create({
    publicKey:prepareCreationOptions(options.publicKey),
  });

  if(!(credential instanceof PublicKeyCredential)){
    throw new Error("Passkey registration was cancelled.");
  }

  const prf=prfResult(credential);

  if(!prf){
    throw new Error(
      "This authenticator cannot create the unlock-capable KNSAIX passkey required for migration."
    );
  }

  return{
    ceremony_id:options.ceremony_id,
    new_credential:serializeRegistrationCredential(credential),
    new_prf_result:prf,
  };
}

